import { FC } from "react";
import Image from "next/image";

import styles from "./GlobalWidgetListPopup.module.css";

import { Button } from "@/components/common";
import { Send } from "@/components/icons";

import { Receipt, ExtendedViewItem } from "@/types/receipt";
import { GlobalWidgetType } from "@/constants/popup";

import {
  getSharingWarningText,
  getActionableButtonText,
  getButtonText,
  getStoreName,
} from "@/utils/extendedViewHelpers";
import { useExtendedServiceHandler } from "@/hooks/useExtendedServiceHandler";
import { useExtendedWidgetVisibility } from "@/hooks/useExtendedWidgetVisibility";
import { useActiveAccountingWidget } from "@/hooks/useActiveAccountingWidget";
import { hasEmailExportFor } from "@/config/emailExportWidgets";
import { EmailExportSection } from "./EmailExportSection";

interface GlobalWidgetListPopupProps {
  widgetType: GlobalWidgetType;
  extendedViewItems: ExtendedViewItem[];
  extendedViewImages: { [key: string]: string };
  lang: string;
  receipt: Receipt;
  user_id?: string;
  receipt_id?: string;
  email?: string | null;
  phoneNumber?: string | null;
  closeButton?: React.ReactNode;
}

export const GlobalWidgetListPopup: FC<GlobalWidgetListPopupProps> = ({
  widgetType,
  extendedViewItems,
  extendedViewImages,
  lang,
  receipt,
  user_id,
  receipt_id,
  email,
  phoneNumber,
  closeButton,
}) => {
  const { handleServiceClick, isLoading } = useExtendedServiceHandler({
    receipt,
    lang,
    user_id,
    receipt_id,
    email,
    phoneNumber,
  });

  const { categorizedGlobalWidgets } = useExtendedWidgetVisibility({
    extended_view_items: extendedViewItems,
    extended_view_images: extendedViewImages,
    currentView: widgetType,
  });

  const { getStoredEmail, onAccountingSendSuccess } =
    useActiveAccountingWidget();

  const widgetsToShow = categorizedGlobalWidgets[widgetType];
  const storeName = getStoreName(receipt);

  return (
    <>
      <div className="box head">
        <h4 className={styles.header}>{getButtonText(widgetType, lang)}</h4>
        {closeButton}

      </div>

      <div className="box">
        {widgetsToShow.length > 0 && (
          <div>
            {widgetsToShow.map((item, index) => {
              const itemKey = `${widgetType}_${index}`;

              const showEmailWarning = item.share_url_email && email;
              const showPhoneWarning =
                item.share_url_phone_number && phoneNumber;
              const hasEmailExport = hasEmailExportFor(
                user_id,
                receipt_id,
                item.receipt_view_service_settings_id
              );

              return (
                <div key={itemKey} className={styles.itemContainer}>
                  <div className={styles.itemContent}>
                    <div className={styles.itemTitle}>{item.title}
                      <div> {item.imageSrc ? <Image
                          src={item.imageSrc}
                          alt={item.title}
                          width={30}
                          height={30}
                          className={styles.buttonIcon}
                        />
                         : undefined}
                        </div>
                        </div>

                    <div className={styles.itemDescription}>
                      {item.description}
                    </div>

                    {user_id && receipt_id && (
                      <EmailExportSection
                        userId={user_id}
                        receiptId={receipt_id}
                        lang={lang}
                        settingsId={item.receipt_view_service_settings_id}
                        storeName={storeName}
                        initialEmail={getStoredEmail(
                          item.receipt_view_service_settings_id
                        )}
                        onSendSuccess={onAccountingSendSuccess(item)}
                      />
                    )}

                    {(showEmailWarning || showPhoneWarning) && (
                      <div className={styles.warningContainer}>
                        {showEmailWarning && (
                          <div className={styles.warningEmail}>
                            {getSharingWarningText(
                              item.extended_view_position,
                              "email",
                              lang
                            )}
                          </div>
                        )}
                        {showPhoneWarning && (
                          <div
                            className={
                              showEmailWarning
                                ? styles.warningPhone
                                : styles.warningPhoneOnly
                            }
                          >
                            {getSharingWarningText(
                              item.extended_view_position,
                              "phone",
                              lang
                            )}
                      
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {!hasEmailExport && (
                    <Button
                      className={styles.button}
                      onClick={() => handleServiceClick(item, itemKey)}
                      text={getActionableButtonText(
                        item.extended_view_position,
                        lang,
                        {
                          service_view_button_text:
                            item.service_view_button_text,
                        }
                      )}
                      isLoading={isLoading(itemKey)}
                      icon={<Send size={16} />}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};
